import React from "react";
import Toast from "../../../components/Toast";
import SelectdDropDown from "../../../components/DropDownComponent";
import CreditActionsManagement from "../CreditActionsManagement";
import TrialConfigManagement from "../TrialConfigManagement";
import { DateFormats } from "../../../utils/formatters";
import { dateFormatDynamic } from "../../../utils/datetime";
import {
  billingCurrencyOptions,
  CreatePlanFormData,
} from "../model/subscription_model";
import { useSubscriptionsViewModel } from "../view_model/subscription_viewmodel";

const SubscriptionsView: React.FC = () => {
  const {
    activeTab,
    closeAddVersionModal,
    closeDeleteScheduledVersionConfirm,
    closeEditModal,
    closeToast,
    closeViewModal,
    createError,
    createPlanForm,
    createSubmitting,
    createSuccess,
    deletingVersionId,
    editError,
    editLoading,
    editPlanForm,
    editSubmitting,
    editingSubscription,
    editVersionError,
    editVersionForm,
    editVersionSubmitting,
    editVersionSuccess,
    error,
    handleAddVersionSubmit,
    handleCreatePlanChange,
    handleCreatePlanSubmit,
    handleDeleteScheduledVersion,
    handleEditPlanChange,
    handleEditPlanSubmit,
    handleEditVersionChange,
    handleViewPlan,
    isScheduledVersion,
    loadSubscriptions,
    loading,
    openAddVersionModal,
    openCreatePlanModal,
    openDeleteScheduledVersionConfirm,
    openEditPlan,
    resetCreateModal,
    setActiveTab,
    showAddVersionModal,
    showCreateModal,
    showToast,
    subscriptions,
    subscriptionTierOptions,
    summary,
    toast,
    versionActionError,
    versionPendingDelete,
    viewError,
    viewLoading,
    viewTierDetails,
    viewTierVersions,
  } = useSubscriptionsViewModel();

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusClasses[status as keyof typeof statusClasses]
        }`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatValue = (value: string | number | boolean | null | undefined) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return String(value);
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return "-";
    }

    return dateFormatDynamic(value, DateFormats.dateTimeAt) || value;
  };

  const renderDetailItem = (label: string, value: React.ReactNode) => (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm text-gray-900">{value}</dd>
    </div>
  );

  const renderBooleanBadge = (value?: boolean | null) => {
    const isEnabled = value === true;
    const label = value === null || value === undefined ? "-" : isEnabled ? "Yes" : "No";

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          isEnabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
        }`}
      >
        {label}
      </span>
    );
  };

  const renderPricingValue = (
    label: string,
    value: React.ReactNode,
    accentClass = "text-gray-900",
  ) => (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className={`mt-2 break-words text-base font-semibold ${accentClass}`}>
        {value}
      </dd>
    </div>
  );

  const getVersionStatusClass = (status?: string | null) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-700";
      case "SCHEDULED":
        return "bg-blue-100 text-blue-700";
      case "EXPIRED":
        return "bg-gray-100 text-gray-700";
      case "DRAFT":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading subscriptions</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadSubscriptions}
                className="mt-2 text-sm text-red-800 underline hover:text-red-900"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Management</h1>
          <p className="text-gray-600">Manage pricing plans, features, and custom discounts</p>
        </div>
        {activeTab === 'plans' && (
          <button
            onClick={openCreatePlanModal}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
          >
            Create New Plan
          </button>
        )}
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab('plans')}
            className={`border-b-2 px-1 pb-3 text-sm font-medium ${
              activeTab === 'plans'
                ? 'border-indigo-500 text-indigo-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Subscription Plans
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('credit-actions')}
            className={`border-b-2 px-1 pb-3 text-sm font-medium ${
              activeTab === 'credit-actions'
                ? 'border-indigo-500 text-indigo-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Credit Actions
          </button>
        </nav>
      </div>

      {activeTab === 'credit-actions' ? (
        <CreditActionsManagement
          tiers={subscriptionTierOptions}
          onNotify={showToast}
        />
      ) : (
        <>
          {/* Summary Statistics */}
          {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-sm font-medium text-gray-500">Total Plans</div>
            <div className="text-2xl font-bold text-gray-900">{summary.total_plans}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-sm font-medium text-gray-500">Active Plans</div>
            <div className="text-2xl font-bold text-green-600">{summary.active_plans}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-sm font-medium text-gray-500">Total Companies</div>
            <div className="text-2xl font-bold text-blue-600">{summary.total_companies}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="text-sm font-medium text-gray-500">Monthly Recurring Revenue</div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.monthly_recurring_revenue)}</div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits Per Dollar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center">
                        {subscription.name}
                        {subscription.is_custom && (
                          <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                            Custom
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {Array.isArray(subscription.features) && subscription.features.length > 0 ? (
                          <>
                            {subscription.features.slice(0, 2).join(', ')}
                            {subscription.features.length > 2 && '...'}
                          </>
                        ) : (
                          'No features listed'
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCurrency(subscription.price_monthly)}/mo
                      {subscription.price_yearly > 0 && (
                        <span className="text-gray-500 ml-2">
                          ({formatCurrency(subscription.price_yearly)}/yr)
                        </span>
                      )}
                    </div>
                    {subscription.yearly_discount_percent && (
                      <div className="text-sm text-green-600">
                        {subscription.yearly_discount_percent}% yearly discount
                      </div>
                    )}
                    {subscription.volume_discount && (
                      <div className="text-sm text-blue-600">
                        Volume: {subscription.volume_discount.discount_percent}% off {subscription.volume_discount.min_users}+ users
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatValue(subscription.credits_per_dollar)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {subscription.active_companies} companies
                    </div>
                    <div className="text-sm text-green-600">
                      {subscription.monthly_subscribers} Monthly / {subscription.yearly_subscribers} Yearly
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(subscription.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleViewPlan(subscription)}
                        className="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                      >
                        View &amp; Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <TrialConfigManagement onNotify={showToast} />
      </div>

      {/* View Plan Modal */}
      {(viewLoading || viewError || viewTierDetails) && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 px-4 py-8">
          <div className="mx-auto max-w-5xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="border-b border-gray-200 bg-white px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {viewTierDetails?.name || 'View Plan'}
                    </h3>
                    {viewTierDetails?.tier_type && (
                      <span className="inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                        {viewTierDetails.tier_type}
                      </span>
                    )}
                    {viewTierDetails?.current_pricing?.status && (
                      <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                        {viewTierDetails.current_pricing.status}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 max-w-3xl text-sm text-gray-500">
                    {viewTierDetails?.description || 'Subscription tier details and active pricing'}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button
                    onClick={() => {
                      if (viewTierDetails) {
                        closeViewModal();
                        openEditPlan({
                          id: viewTierDetails.id,
                          name: viewTierDetails.name || 'Subscription Plan',
                        });
                      }
                    }}
                    className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!viewTierDetails}
                  >
                    Edit plan
                  </button>
                  <button
                    onClick={closeViewModal}
                    className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close view plan"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div className="max-h-[78vh] overflow-y-auto px-6 py-6">
              {viewLoading && (
                <div className="flex justify-center items-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                </div>
              )}

              {viewError && !viewLoading && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {viewError}
                </div>
              )}

              {viewTierDetails && !viewLoading && (
                <div className="space-y-6">
                  <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {renderPricingValue(
                      'Monthly Price',
                      formatValue(viewTierDetails.current_pricing?.price_monthly),
                      'text-indigo-700'
                    )}
                    {renderPricingValue(
                      'Yearly Price',
                      formatValue(viewTierDetails.current_pricing?.price_yearly),
                      'text-indigo-700'
                    )}
                    {renderPricingValue(
                      'Credits Per Dollar',
                      formatValue(viewTierDetails.current_pricing?.credits_per_dollar),
                      'text-green-700'
                    )}
                  </section>

                  <section className="rounded-lg border border-gray-200 bg-white p-5">
                    <h4 className="mb-4 text-sm font-semibold text-gray-900">Tier Details</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {renderDetailItem('Tier Type', formatValue(viewTierDetails.tier_type))}
                    {renderDetailItem('Slug', formatValue(viewTierDetails.slug))}
                    {renderDetailItem('Name', formatValue(viewTierDetails.name))}
                    {renderDetailItem('Description', formatValue(viewTierDetails.description))}
                    {renderDetailItem('Active', renderBooleanBadge(viewTierDetails.is_active))}
                    {renderDetailItem('Public', renderBooleanBadge(viewTierDetails.is_public))}
                    {renderDetailItem('Sort Order', formatValue(viewTierDetails.sort_order))}
                    {renderDetailItem('Badge Text', formatValue(viewTierDetails.badge_text))}
                    {renderDetailItem('Created At', formatDateTime(viewTierDetails.created_at))}
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 md:col-span-2 lg:col-span-3">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Features</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {Array.isArray(viewTierDetails.features)
                          ? viewTierDetails.features.length > 0
                            ? viewTierDetails.features.join(', ')
                            : '-'
                          : formatValue(viewTierDetails.features)}
                      </dd>
                    </div>
                    </dl>
                  </section>

                  <section className="rounded-lg border border-gray-200 bg-white p-5">
                    <h4 className="mb-4 text-sm font-semibold text-gray-900">Current Pricing</h4>
                    <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {renderDetailItem('Monthly Price', formatValue(viewTierDetails.current_pricing?.price_monthly))}
                    {renderDetailItem('Yearly Price', formatValue(viewTierDetails.current_pricing?.price_yearly))}
                    {renderDetailItem('Currency', formatValue(viewTierDetails.current_pricing?.currency))}
                    {renderDetailItem('Credits Per Dollar', formatValue(viewTierDetails.current_pricing?.credits_per_dollar))}
                    {renderDetailItem('Topup Discount Percent', formatValue(viewTierDetails.current_pricing?.topup_discount_percent))}
                    {renderDetailItem('Yearly Bonus Percent', formatValue(viewTierDetails.current_pricing?.yearly_bonus_percent))}
                    {renderDetailItem('Monthly Bonus Percent', formatValue(viewTierDetails.current_pricing?.monthly_bonus_percent))}
                    {renderDetailItem('Version Number', formatValue(viewTierDetails.current_pricing?.version_number))}
                    {renderDetailItem('Effective From', formatDateTime(viewTierDetails.current_pricing?.effective_from))}
                    {renderDetailItem('Effective Until', formatDateTime(viewTierDetails.current_pricing?.effective_until))}
                    {renderDetailItem('Status', formatValue(viewTierDetails.current_pricing?.status))}
                    {renderDetailItem('Stripe Monthly Price ID', formatValue(viewTierDetails.current_pricing?.stripe_price_id_monthly))}
                    {renderDetailItem('Stripe Yearly Price ID', formatValue(viewTierDetails.current_pricing?.stripe_price_id_yearly))}
                    {renderDetailItem('Stripe Product ID', formatValue(viewTierDetails.current_pricing?.stripe_product_id))}
                    {renderDetailItem('Notes', formatValue(viewTierDetails.current_pricing?.notes))}
                    {renderDetailItem('Created At', formatDateTime(viewTierDetails.current_pricing?.created_at))}
                    </dl>
                  </section>

                  <section className="rounded-lg border border-gray-200 bg-white p-5">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Version History</h4>
                        <p className="text-xs text-gray-500">Pricing versions returned from the tier history endpoint</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                          {viewTierVersions.length} versions
                        </span>
                        <button
                          onClick={openAddVersionModal}
                          className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                        >
                          Add new pricing version
                        </button>
                      </div>
                    </div>

                    {viewTierVersions.length === 0 ? (
                      <div className="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                        No version history available.
                      </div>
                    ) : (
                      <>
                        {versionActionError && (
                          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {versionActionError}
                          </div>
                        )}
                        <div className="space-y-4">
                          {viewTierVersions.map((version, index) => (
                            <div
                              key={version.id || `${version.version_number || 'version'}-${index}`}
                              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                            >
                              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h5 className="text-sm font-semibold text-gray-900">
                                    Version {formatValue(version.version_number)}
                                  </h5>
                                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${getVersionStatusClass(version.status)}`}>
                                    {formatValue(version.status)}
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                  <div className="text-xs text-gray-500">
                                    Created {formatDateTime(version.created_at)}
                                  </div>
                                  {isScheduledVersion(version) && version.id && (
                                    <button
                                      onClick={() => openDeleteScheduledVersionConfirm(version)}
                                      className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                      disabled={deletingVersionId === version.id}
                                    >
                                      {deletingVersionId === version.id ? 'Cancelling...' : 'Cancel Schedule'}
                                    </button>
                                  )}
                                </div>
                              </div>

                              <dl className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                                {renderDetailItem('Monthly Price', formatValue(version.price_monthly))}
                                {renderDetailItem('Yearly Price', formatValue(version.price_yearly))}
                                {renderDetailItem('Currency', formatValue(version.currency))}
                                {renderDetailItem('Credits Per Dollar', formatValue(version.credits_per_dollar))}
                                {renderDetailItem('Topup Discount Percent', formatValue(version.topup_discount_percent))}
                                {renderDetailItem('Yearly Bonus Percent', formatValue(version.yearly_bonus_percent))}
                                {renderDetailItem('Monthly Bonus Percent', formatValue(version.monthly_bonus_percent))}
                                {renderDetailItem('Effective From', formatDateTime(version.effective_from))}
                                {renderDetailItem('Effective Until', formatDateTime(version.effective_until))}
                                {renderDetailItem('Stripe Monthly Price ID', formatValue(version.stripe_price_id_monthly))}
                                {renderDetailItem('Stripe Yearly Price ID', formatValue(version.stripe_price_id_yearly))}
                                {renderDetailItem('Stripe Product ID', formatValue(version.stripe_product_id))}
                                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 md:col-span-2 lg:col-span-3">
                                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Notes</dt>
                                  <dd className="mt-1 break-words text-sm text-gray-900">{formatValue(version.notes)}</dd>
                                </div>
                              </dl>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </section>

                  <div className="flex justify-end border-t border-gray-200 pt-4">
                    <button
                      onClick={closeViewModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {versionPendingDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900 bg-opacity-60 px-4">
          <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="px-6 py-5">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Cancel scheduled version?</h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Are you sure you want to cancel the schedule for Version {formatValue(versionPendingDelete.version_number)}? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-3 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeDeleteScheduledVersionConfirm}
                disabled={Boolean(deletingVersionId)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteScheduledVersion}
                disabled={Boolean(deletingVersionId)}
                className="rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
              >
                {deletingVersionId ? 'Cancelling...' : 'Cancel Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tier Version Modal */}
      {showAddVersionModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-gray-900 bg-opacity-50 px-4 py-8">
          <div className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Tier Version
                </h3>
                <p className="text-sm text-gray-500">Add new pricing version</p>
              </div>
              <button
                onClick={closeAddVersionModal}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close add tier version"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form className="max-h-[78vh] overflow-y-auto px-6 py-6" onSubmit={handleAddVersionSubmit}>
              <div className="space-y-5">
                {editVersionError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {editVersionError}
                  </div>
                )}
                {editVersionSuccess && (
                  <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                    {editVersionSuccess}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Price *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={editVersionForm.price_monthly}
                      onChange={(event) => handleEditVersionChange('price_monthly', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yearly Price *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={editVersionForm.price_yearly}
                      onChange={(event) => handleEditVersionChange('price_yearly', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <SelectdDropDown
                      id="tier_version_currency"
                      label="Currency"
                      required
                      value={editVersionForm.currency}
                      placeholder="Select Currency"
                      options={billingCurrencyOptions}
                      onChange={(value) => handleEditVersionChange('currency', value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credits Per Dollar *
                    </label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={editVersionForm.credits_per_dollar}
                      onChange={(event) => handleEditVersionChange('credits_per_dollar', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Topup Discount Percent
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={editVersionForm.topup_discount_percent}
                      onChange={(event) => handleEditVersionChange('topup_discount_percent', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yearly Bonus Percent
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={editVersionForm.yearly_bonus_percent}
                      onChange={(event) => handleEditVersionChange('yearly_bonus_percent', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Bonus Percent
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={editVersionForm.monthly_bonus_percent}
                      onChange={(event) => handleEditVersionChange('monthly_bonus_percent', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Effective From *
                    </label>
                    <input
                      type="date"
                      required
                      value={editVersionForm.effective_from}
                      onChange={(event) => handleEditVersionChange('effective_from', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stripe Monthly Price ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={editVersionForm.stripe_price_id_monthly}
                      onChange={(event) => handleEditVersionChange('stripe_price_id_monthly', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stripe Yearly Price ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={editVersionForm.stripe_price_id_yearly}
                      onChange={(event) => handleEditVersionChange('stripe_price_id_yearly', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stripe Product ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={editVersionForm.stripe_product_id}
                      onChange={(event) => handleEditVersionChange('stripe_product_id', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      value={editVersionForm.notes}
                      onChange={(event) => handleEditVersionChange('notes', event.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={closeAddVersionModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    disabled={editVersionSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                    disabled={editVersionSubmitting}
                  >
                    {editVersionSubmitting ? 'Creating...' : 'Create Version'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingSubscription) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative p-5 top-10 mx-auto w-11/12 md:w-3/4 lg:w-1/2 max-h-[90vh] rounded-md border bg-white shadow-lg flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingSubscription ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
              </h3>
              <button
                onClick={() => {
                  if (showCreateModal) {
                    resetCreateModal();
                  } else {
                    closeEditModal();
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5">
            {editingSubscription ? (
              <form className="space-y-5" onSubmit={handleEditPlanSubmit}>
                {editLoading && (
                  <div className="flex justify-center items-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                  </div>
                )}

                {editError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {editError}
                  </div>
                )}
                {!editLoading && (
                  <>
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                      <h4 className="text-base font-semibold text-gray-900 mb-4">
                        Plan Metadata
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Plan Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={editPlanForm.name}
                            onChange={(event) => handleEditPlanChange('name', event.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="e.g., Professional Plan"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Slug *
                          </label>
                          <input
                            type="text"
                            required
                            pattern="[a-z0-9_-]+"
                            value={editPlanForm.slug}
                            onChange={(event) => handleEditPlanChange('slug', event.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="professional"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            rows={3}
                            value={editPlanForm.description}
                            onChange={(event) => handleEditPlanChange('description', event.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Plan description"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sort Order
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={editPlanForm.sort_order}
                            onChange={(event) => handleEditPlanChange('sort_order', Number(event.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Badge Text
                          </label>
                          <input
                            type="text"
                            value={editPlanForm.badge_text}
                            onChange={(event) => handleEditPlanChange('badge_text', event.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Best Value"
                          />
                        </div>

                        <div className="flex items-center pt-2">
                          <input
                            type="checkbox"
                            id="editIsActive"
                            checked={editPlanForm.is_active}
                            onChange={(event) => handleEditPlanChange('is_active', event.target.checked)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <label htmlFor="editIsActive" className="ml-2 block text-sm text-gray-700">
                            Active plan
                          </label>
                        </div>

                        <div className="flex items-center pt-2">
                          <input
                            type="checkbox"
                            id="editIsPublic"
                            checked={editPlanForm.is_public}
                            onChange={(event) => handleEditPlanChange('is_public', event.target.checked)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <label htmlFor="editIsPublic" className="ml-2 block text-sm text-gray-700">
                            Public plan
                          </label>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Features
                          </label>
                          <textarea
                            rows={4}
                            value={editPlanForm.features}
                            onChange={(event) => handleEditPlanChange('features', event.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter one feature per line"
                          />
                          <p className="text-xs text-gray-500 mt-1">Enter one feature per line</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={closeEditModal}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        disabled={editSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                        disabled={editSubmitting}
                      >
                        {editSubmitting ? 'Updating...' : 'Update Plan'}
                      </button>
                    </div>
                  </>
                )}
              </form>
            ) : (
              <form className="space-y-5" onSubmit={handleCreatePlanSubmit}>
                {createError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {createError}
                  </div>
                )}
                {createSuccess && (
                  <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                    {createSuccess}
                  </div>
                )}

                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h4 className="text-base font-semibold text-gray-900 mb-4">
                    Create New Subscription Plan
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tier Type
                      </label>
                      <select
                        value={createPlanForm.tier_type}
                        onChange={(event) => handleCreatePlanChange('tier_type', event.target.value as CreatePlanFormData['tier_type'])}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="STARTER">STARTER</option>
                        <option value="PRO">PRO</option>
                        <option value="ENTERPRISE">ENTERPRISE</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plan Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={createPlanForm.name}
                        onChange={(event) => handleCreatePlanChange('name', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Pro Plus"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Slug *
                      </label>
                      <input
                        type="text"
                        required
                        pattern="[a-z0-9_-]+"
                        value={createPlanForm.slug}
                        onChange={(event) => handleCreatePlanChange('slug', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="pro-plus"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sort Order
                      </label>
                      <select
                        value={createPlanForm.sort_order}
                        onChange={(event) => handleCreatePlanChange('sort_order', Number(event.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={3}
                        value={createPlanForm.description}
                        onChange={(event) => handleCreatePlanChange('description', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Plan description"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Features
                      </label>
                      <textarea
                        rows={4}
                        value={createPlanForm.features}
                        onChange={(event) => handleCreatePlanChange('features', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter one feature per line"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Badge Text
                      </label>
                      <input
                        type="text"
                        value={createPlanForm.badge_text}
                        onChange={(event) => handleCreatePlanChange('badge_text', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="New"
                      />
                    </div>
                    <div className="flex items-center pt-7">
                      <input
                        type="checkbox"
                        id="isPublic"
                        checked={createPlanForm.is_public}
                        onChange={(event) => handleCreatePlanChange('is_public', event.target.checked)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                        Public plan
                      </label>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h4 className="text-base font-semibold text-gray-900 mb-4">
                    Initial Pricing Tier Version
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Monthly Price *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={createPlanForm.price_monthly}
                        onChange={(event) => handleCreatePlanChange('price_monthly', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="199.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Yearly Price *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={createPlanForm.price_yearly}
                        onChange={(event) => handleCreatePlanChange('price_yearly', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="1990.00"
                      />
                    </div>
                    <div>
                      <SelectdDropDown
                        id="initial_pricing_currency"
                        label="Currency"
                        required
                        value={createPlanForm.currency}
                        placeholder="Select Currency"
                        options={billingCurrencyOptions}
                        onChange={(value) => handleCreatePlanChange('currency', value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Credits Per Dollar *
                      </label>
                      <input
                        type="number"
                        min="1"
                        required
                        value={createPlanForm.credits_per_dollar}
                        onChange={(event) => handleCreatePlanChange('credits_per_dollar', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Yearly Bonus Percent
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={createPlanForm.yearly_bonus_percent}
                        onChange={(event) => handleCreatePlanChange('yearly_bonus_percent', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Monthly Bonus Percent
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={createPlanForm.monthly_bonus_percent}
                        onChange={(event) => handleCreatePlanChange('monthly_bonus_percent', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Topup Discount Percent
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={createPlanForm.topup_discount_percent}
                        onChange={(event) => handleCreatePlanChange('topup_discount_percent', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Effective From *
                      </label>
                      <input
                        type="date"
                        required
                        value={createPlanForm.effective_from}
                        onChange={(event) => handleCreatePlanChange('effective_from', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stripe Monthly Price ID *
                      </label>
                      <input
                        type="text"
                        required
                        value={createPlanForm.stripe_price_id_monthly}
                        onChange={(event) => handleCreatePlanChange('stripe_price_id_monthly', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="price_xxx"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stripe Yearly Price ID *
                      </label>
                      <input
                        type="text"
                        required
                        value={createPlanForm.stripe_price_id_yearly}
                        onChange={(event) => handleCreatePlanChange('stripe_price_id_yearly', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="price_yyy"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Stripe Product ID *
                      </label>
                      <input
                        type="text"
                        required
                        value={createPlanForm.stripe_product_id}
                        onChange={(event) => handleCreatePlanChange('stripe_product_id', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="prod_xxx"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        rows={3}
                        value={createPlanForm.notes}
                        onChange={(event) => handleCreatePlanChange('notes', event.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Initial launch pricing"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetCreateModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    disabled={createSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                    disabled={createSubmitting}
                  >
                    {createSubmitting ? 'Creating...' : 'Create Plan'}
                  </button>
                </div>
              </form>
            )}
            </div>
          </div>
        </div>
      )}
        </>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default SubscriptionsView;
